<script lang="ts">
  /**
   * Imports
   * ---------------------------------------------------------------------------
   */

  import {
    embeddedMediaSeries,
    determineMediaType,
  } from "@utils/subsplashMedia.ts";
  import type { MediaItem } from "@customTypes/subsplash";
  import LayeredSection from "../layout/LayeredSection.svelte";
  import Duration from "../media/Duration.svelte";
  import { isBackgroundPlayerPaused } from "../../stores/playerStore.ts";
  import PublishDate from "../media/PublishDate.svelte";
  import ShareMedia from "../media/ShareMedia.svelte";
  import Button from "@elements/Button.svelte";
  import slugify from "slugify";
  /**
   * Props
   * ---------------------------------------------------------------------------
   */

  export let podcastShowSlug: string;
  export let item: MediaItem;
  export let nextItem: MediaItem;
  export let previousItem: MediaItem;

  /**
   * Data, State
   * ---------------------------------------------------------------------------
   */
  let video: HTMLVideoElement;
  $: video && ($isBackgroundPlayerPaused ? video.pause() : video.play());

  const series = embeddedMediaSeries(item);
  const seriesTitle = series?.title;
  const mediaType = determineMediaType(item);
  const shortCode = item.short_code;
  const title = item.title;
  const speaker = item.speaker;
  const publishedAt = item.date ? new Date(item.date) : undefined;
  const itemNumber = !!item.position ? parseInt(item.position, 10) : undefined;
  /** A link to the podcast series page. */
  const seriesLink = `/podcasts/${podcastShowSlug}`;
  /** A link to the next item in the series. Undefined if there is no next item. */
  const nextLink =
    item.id !== nextItem.id
      ? `/podcasts/${podcastShowSlug}/${nextItem.slug}`
      : undefined;
  /** A link to the previous item in the series. Undefined if there is no previous item. */
  const prevLink =
    item.id !== previousItem.id
      ? `/podcasts/${podcastShowSlug}/${previousItem.slug}`
      : undefined;
  /** Duration of audio/video in milliseconds */
  const durationMs =
    item._embedded?.video?.duration ||
    item._embedded?.audio?._embedded["audio-outputs"][0].duration;
  const summaryHTML = item.summary;
  const summaryText = item.summary_text;
  const documentTitle = item.title;
  const isSummaryTextLong = summaryText && summaryText.length > 300;

  const documentLink = (item["_embedded"]?.audio?.["_embedded"]?.[
    "audio-outputs"
  ][0]["_links"]?.download?.href).replace("{filename}", slugify(item.title));
</script>

<LayeredSection
  bind:video
  addClasses="box-border flex items-center justify-start overflow-hidden bg-cover bg-center"
  layers={[
    {
      backgroundCSS:
        "background-blend-mode: normal, normal, color, color-dodge, normal; background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 65.62%, #010101 100%), linear-gradient(0deg, rgba(1, 1, 1, 0.10) 0%, rgba(1, 1, 1, 0.10) 100%);",
    },
  ]}
>
  <div
    class="@container pt-48 pb-24 container w-full z-10 text-white gap-6 md:gap-x-16 grid grid-cols-1 md:grid-cols-2"
  >
    <div class="row-start-2 col-span-2 md:row-start-1 md:col-span-1">
      {#if seriesTitle}
        <a href={seriesLink}>
          <h6 class="text-p100 mb-4">{seriesTitle}</h6>
        </a>
      {/if}
      {#if title}
        <h1
          class="text-white normal-case font-bold mb-4"
          data-pagefind-filter={`type:${mediaType}`}
        >
          {title}
        </h1>
      {/if}
      {#if speaker}
        <h3 class="mb-4">{speaker}</h3>
      {/if}
      {#if publishedAt}
        <p class="wah-body-small mb-4">
          <PublishDate dateTime={publishedAt} />
          {#if itemNumber}
            <span class="text-s100">&nbsp;|&nbsp;</span>
            <span>Item {itemNumber}</span>
          {/if}
          {#if durationMs}
            <span class="text-s100">&nbsp;|&nbsp;</span>
            <Duration durationInMs={durationMs} />
          {/if}
        </p>
      {/if}
      {#if summaryHTML && !isSummaryTextLong}
        <p class="text-left mb-4 mt-8">
          {@html summaryHTML}
        </p>
      {/if}
      <div class="flex gap-6">
        <ShareMedia {title} />
        {#if documentTitle && documentLink}
          <Button
            class="text-white fill-white stroke-white"
            variant="icon"
            href={documentLink}
            leftIcon={{ name: "download" }}
            text="Download"
          />
        {/if}
      </div>
      <p class="wah-body-small mt-4">
        <span>
          {#if prevLink}<a href={prevLink}>Previous Episode</a>
          {:else}<span class="text-s100">Previous Episode</span>{/if}
          <span class="text-s100">&nbsp;|&nbsp;</span>

          {#if nextLink}<a href={nextLink}>Next Episode</a>
          {:else}<span class="text-s100">Next Episode</span>{/if}
        </span>
      </p>
    </div>
    {#if shortCode}
      <div class="row-start-1 col-span-2 md:row-start-auto md:col-span-1">
        <div class="sap-embed-player">
          <iframe
            src={`https://subsplash.com/+f83c/embed/mi/+${shortCode}?audio&video&logoWatermark&shareable&embeddable`}
            frameborder="0"
            webkitallowfullscreen
            mozallowfullscreen
            allowfullscreen
          ></iframe>
        </div>
      </div>
    {/if}
    {#if summaryHTML && isSummaryTextLong}
      <div class="row-start-3 col-span-2">
        <p class="text-left mb-4 [&_a]:text-p100">
          {@html summaryHTML}
        </p>
      </div>
    {/if}
  </div>
</LayeredSection>

<style type="text/css">
  div.sap-embed-player {
    position: relative;
    width: 100%;
    height: 0;
    padding-top: 56.25%;
  }
  div.sap-embed-player > iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
</style>
